import React, { useState, useEffect, useMemo } from 'react';
import { FaMobileAlt, FaShare, FaEllipsisV } from 'react-icons/fa';
import './InstallAppPrompt.css';

const STORAGE_KEY = 'installPromptDismissedAt';
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours
const SHOW_DELAY_MS = 2000;

/**
 * Platform detection via user-agent and navigator.platform
 * Returns: 'ios' | 'android' | 'desktop'
 */
const getPlatform = () => {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';

  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  if (platform === 'MacIntel' && navigator.maxTouchPoints > 1) return 'ios';
  if (/Android/i.test(ua)) return 'android';

  return 'desktop';
};

const wasDismissedRecently = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const at = parseInt(raw, 10);
    return Date.now() - at < DISMISS_DURATION_MS;
  } catch {
    return false;
  }
};

/** Call prompt() synchronously so the browser still counts it as a user gesture. */
export const tryNativeInstallSync = () => {
  const deferredPrompt = window.__installPrompt;
  if (!deferredPrompt) return false;

  try {
    deferredPrompt.prompt();
    deferredPrompt.userChoice
      .then(() => {
        window.__installPrompt = null;
      })
      .catch(() => {
        window.__installPrompt = null;
      });
    return true;
  } catch (e) {
    console.warn('Install prompt error:', e);
    return false;
  }
};

export const showInstallPrompt = () => {
  if (tryNativeInstallSync()) return Promise.resolve(true);
  window.dispatchEvent(new Event('showInstallPrompt'));
  return Promise.resolve(false);
};

const InstallAppPrompt = () => {
  const platform = useMemo(() => getPlatform(), []);
  const [view, setView] = useState('hidden'); // hidden | promo | instructions

  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    if (isStandalone) return;

    const onManualShow = () => {
      if (tryNativeInstallSync()) return;
      setView('promo');
    };
    const onAppInstalled = () => setView('hidden');
    const timer = setTimeout(() => {
      if (!wasDismissedRecently()) setView('promo');
    }, SHOW_DELAY_MS);

    window.addEventListener('showInstallPrompt', onManualShow);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('showInstallPrompt', onManualShow);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const handleNotNow = () => {
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch (e) {}
    setView('hidden');
  };

  const handleInstall = () => {
    if (tryNativeInstallSync()) {
      setView('hidden');
      return;
    }
    setView('instructions');
  };

  if (view === 'hidden') return null;

  if (view === 'instructions' || platform === 'ios') {
    if (platform === 'ios') {
      return (
        <div className="install-prompt-wrap" role="dialog" aria-label="Install app - iOS">
          <div className="install-prompt-card install-prompt-card-instructions">
            <div className="install-prompt-icon">
              <FaShare />
            </div>
            <div className="install-prompt-text">
              <h3 className="install-prompt-title">Install UBSAAPP on iPhone/iPad</h3>
              <p className="install-prompt-desc">Tap Share → Add to Home Screen</p>
              <ol className="install-prompt-steps" aria-label="Installation steps">
                <li>Tap the <strong>Share</strong> button <FaShare className="install-prompt-inline-icon" /> at the bottom of Safari</li>
                <li>Scroll down and tap <strong>Add to Home Screen</strong></li>
                <li>Tap <strong>Add</strong> in the top right</li>
              </ol>
              <p className="install-prompt-desc">The app icon will appear on your home screen. Open it anytime to book stays.</p>
            </div>
            <button type="button" className="install-prompt-btn install-prompt-btn-primary" onClick={handleNotNow}>
              Done
            </button>
          </div>
        </div>
      );
    }

    if (platform === 'android') {
      return (
        <div className="install-prompt-wrap" role="dialog" aria-label="Install app - Android">
          <div className="install-prompt-card install-prompt-card-instructions">
            <div className="install-prompt-icon">
              <FaEllipsisV />
            </div>
            <div className="install-prompt-text">
              <h3 className="install-prompt-title">Install UBSAAPP on Android</h3>
              <p className="install-prompt-desc">Add the app to your home screen from Chrome</p>
              <ol className="install-prompt-steps" aria-label="Installation steps">
                <li>Tap the <strong>menu</strong> <FaEllipsisV className="install-prompt-inline-icon" /> (three dots) in the top-right of Chrome</li>
                <li>Tap <strong>Install app</strong> or <strong>Add to Home screen</strong></li>
                <li>Tap <strong>Install</strong> to confirm</li>
              </ol>
              <p className="install-prompt-desc">The UBSAAPP icon will appear on your home screen.</p>
            </div>
            <button type="button" className="install-prompt-btn install-prompt-btn-primary" onClick={handleNotNow}>
              Done
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="install-prompt-wrap" role="dialog" aria-label="Install app - Desktop">
        <div className="install-prompt-card install-prompt-card-instructions">
          <div className="install-prompt-icon">
            <FaMobileAlt />
          </div>
          <div className="install-prompt-text">
            <h3 className="install-prompt-title">Install UBSAAPP on your computer</h3>
            <p className="install-prompt-desc">Install from your browser menu or address bar</p>
            <ol className="install-prompt-steps" aria-label="Installation steps">
              <li>Look for the <strong>install icon</strong> in the address bar (computer with down arrow)</li>
              <li>Or open the browser menu and choose <strong>Install UBSAAPP</strong></li>
              <li>Click <strong>Install</strong> in the dialog</li>
            </ol>
            <p className="install-prompt-desc">Open UBSAAPP anytime from your desktop or taskbar.</p>
          </div>
          <button type="button" className="install-prompt-btn install-prompt-btn-primary" onClick={handleNotNow}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="install-prompt-wrap" role="dialog" aria-label="Install app">
      <div className="install-prompt-card">
        <div className="install-prompt-icon">
          <FaMobileAlt />
        </div>
        <div className="install-prompt-text">
          <h3 className="install-prompt-title">Get the UBSAAPP app</h3>
          <p className="install-prompt-desc">
            Book stays on your phone. Download the app.
          </p>
        </div>
        <div className="install-prompt-actions">
          <button type="button" className="install-prompt-btn install-prompt-btn-primary" onClick={handleInstall}>
            Install
          </button>
          <button type="button" className="install-prompt-btn install-prompt-btn-secondary" onClick={handleNotNow}>
            Not now
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallAppPrompt;
